import { FormTextField, TextFieldProps } from 'omakase';
import { useFormikContext } from 'formik';
import Anchor from 'components/Anchor';
import React from 'react';
import emailSpellChecker from '@zootools/email-spell-checker';

type FormEmailFieldOwnProps = {
  onUseSuggestion?(): void;
};

type FormEmailFieldProps = FormEmailFieldOwnProps & TextFieldProps;

/**
 * Formik-ready text input that enables email mistype-correction
 */
const FormEmailField: React.FunctionComponent<FormEmailFieldProps> = ({
  name = 'email',
  id = 'email',
  label = 'Email',
  inputProps,
  placeholder = 'Email address',
  autoComplete = 'email',
  onUseSuggestion,
  ...rest
}) => {
  const [suggestion, setSuggestion] = React.useState<string | undefined>(undefined);
  const formik = useFormikContext();

  const testid = inputProps ? inputProps['data-testid'] : 'email-input';

  const resetSuggestion = () => {
    setSuggestion(undefined);
  };

  const handleUseMailcheckSuggestion = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    formik.setFieldValue(name, suggestion);
    resetSuggestion();
    if (onUseSuggestion) {
      onUseSuggestion();
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    const suggestion = emailSpellChecker.run({ email: value });
    if (suggestion) {
      setSuggestion(suggestion.full);
    } else {
      resetSuggestion();
    }
  };

  const mailcheckSuggestion = suggestion ? (
    <>
      Did you mean{' '}
      <Anchor href="#" onClick={handleUseMailcheckSuggestion}>
        {suggestion}
      </Anchor>
      ?
    </>
  ) : undefined;

  return (
    <FormTextField
      name={name}
      id={id}
      type="email"
      placeholder={placeholder}
      autoComplete={autoComplete}
      label={label}
      onBlur={handleBlur}
      inputProps={{
        'data-testid': testid,
      }}
      helperText={mailcheckSuggestion}
      {...rest}
    />
  );
};

export default FormEmailField;
